import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Upload,
  AlertTriangle,
  Edit3,
  Eye,
  CalendarOff,
  Clock,
  Pencil,
  RefreshCw,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import schedules from "@/api/schedules";
import { useAuth } from "@/contexts/AuthContext";
import ScheduleUploadModal from "@/components/schedule/ScheduleUploadModal";
import AnomaliesPanel from "@/components/schedule/AnomaliesPanel";
import CellEditPopover from "@/components/schedule/CellEditPopover";
import { ROLES, ROLE_ICON, ROLE_BADGE_CLASS, roleLabel } from "@/components/schedule/roles";

/**
 * Phase 4.9 — Weekly Schedule page.
 *
 * Replicates the customer's xlsx grid: rows grouped by section, columns =
 * 7 days. Cells show shifts or "OFF". Edit mode toggles inline editing.
 */
function fmtHeader(dateStr, locale) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString(locale || "en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function fmtTime12(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr12 = h % 12 || 12;
  return `${hr12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function Schedule() {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("schedule:manage");
  const locale = i18n.language === "es" ? "es-MX" : "en-US";

  const [list, setList] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [anomaliesOpen, setAnomaliesOpen] = useState(false);
  const [openCellId, setOpenCellId] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");

  const refresh = async (id) => {
    setLoading(true);
    try {
      const all = (await schedules.list(false)).data || [];
      setList(all);
      const targetId = id || (all.find((s) => s.active) || all[0])?.id;
      if (!targetId) {
        setData(null);
        setCurrentId(null);
        return;
      }
      const full = (await schedules.byId(targetId)).data;
      setData(full);
      setCurrentId(targetId);
    } catch {
      toast.error(t("common.errorLoading"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build a (section, employee_name_norm) → [entry per day_date] grid.
  const grid = useMemo(() => {
    if (!data) return null;
    const sectionOrder = (data.sections || [])
      .slice()
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const days = data.days || [];

    // Group entries by section → employee. The role is an employee-level
    // attribute (same value across all days), so we just take whatever
    // role we see on the first entry of that employee.
    const bySection = new Map();
    (data.entries || []).forEach((e) => {
      const secName = e.section_name || "TOP";
      if (!bySection.has(secName)) bySection.set(secName, new Map());
      const empMap = bySection.get(secName);
      if (!empMap.has(e.employee_name_norm)) {
        empMap.set(e.employee_name_norm, {
          name: e.employee_name,
          role: e.role || "regular",
          days: {},
        });
      }
      empMap.get(e.employee_name_norm).days[e.day_date] = e;
    });

    // Output ordered rows, applying the role filter.
    const rows = [];
    sectionOrder.forEach((sec) => {
      const empMap = bySection.get(sec.name);
      if (!empMap || empMap.size === 0) return;
      const filtered = Array.from(empMap.entries())
        .filter(([, info]) =>
          roleFilter === "all" ? true : (info.role || "regular") === roleFilter
        );
      if (filtered.length === 0) return;
      rows.push({ kind: "section", section: sec });
      filtered
        .sort((a, b) => (a[1].name || "").localeCompare(b[1].name || ""))
        .forEach(([norm, info]) => {
          rows.push({
            kind: "employee",
            name: info.name,
            norm,
            role: info.role || "regular",
            days: info.days,
          });
        });
    });
    return { rows, days };
  }, [data, roleFilter]);

  // Total scheduled hours of the current schedule.
  const totalHours = useMemo(() => {
    if (!data?.entries) return 0;
    let total = 0;
    for (const e of data.entries) {
      if (e.is_off || !e.time_in || !e.time_out) continue;
      const [ih, im] = e.time_in.split(":").map(Number);
      const [oh, om] = e.time_out.split(":").map(Number);
      const mins = oh * 60 + om - (ih * 60 + im);
      if (mins > 0) total += mins;
    }
    return Math.round((total / 60) * 100) / 100;
  }, [data]);

  const handleSaveCell = async (entry, patch) => {
    try {
      const r = await schedules.patchEntry(data.id, entry.id, patch);
      setData((prev) => ({
        ...prev,
        entries: prev.entries.map((e) =>
          e.id === entry.id ? { ...e, ...r.data } : e
        ),
      }));
      toast.success(t("schedule.cell.saved"));
    } catch {
      toast.error(t("schedule.cell.saveError"));
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Calendar className="h-6 w-6 text-primary" />
            {t("schedule.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {data
              ? `${data.week_start} → ${data.week_end} · ${
                  data.source_filename || ""
                }`
              : t("schedule.noData")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {list.length > 0 && (
            <Select
              value={currentId || ""}
              onValueChange={(v) => refresh(v)}
            >
              <SelectTrigger
                className="h-9 w-[200px] text-xs"
                data-testid="schedule-week-select"
              >
                <SelectValue placeholder={t("schedule.selectWeek")} />
              </SelectTrigger>
              <SelectContent>
                {list.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.week_start} → {s.week_end}
                    {s.active ? " ●" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {data && (
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger
                className="h-9 w-[160px] text-xs"
                data-testid="schedule-role-filter"
              >
                <SelectValue placeholder={t("schedule.role.filterAll")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("schedule.role.filterAll")}</SelectItem>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_ICON[r]} {roleLabel(t, r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refresh(currentId)}
            data-testid="schedule-refresh"
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            {t("common.refresh")}
          </Button>
          {data && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAnomaliesOpen(true)}
              data-testid="schedule-anomalies-btn"
            >
              <AlertTriangle className="mr-1.5 h-3.5 w-3.5 text-amber-500" />
              {t("schedule.anomalies.short")}
            </Button>
          )}
          {canManage && data && (
            <Button
              variant={editMode ? "default" : "outline"}
              size="sm"
              onClick={() => setEditMode((v) => !v)}
              data-testid="schedule-edit-toggle"
            >
              {editMode ? (
                <>
                  <Eye className="mr-1.5 h-3.5 w-3.5" />
                  {t("schedule.viewMode")}
                </>
              ) : (
                <>
                  <Edit3 className="mr-1.5 h-3.5 w-3.5" />
                  {t("schedule.editMode")}
                </>
              )}
            </Button>
          )}
          {canManage && (
            <Button
              size="sm"
              onClick={() => setUploadOpen(true)}
              data-testid="schedule-upload-btn"
            >
              <Upload className="mr-1.5 h-3.5 w-3.5" />
              {t("schedule.upload.short")}
            </Button>
          )}
        </div>
      </div>

      {loading && (
        <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
          {t("common.loading")}
        </div>
      )}

      {!loading && !data && (
        <Card data-testid="schedule-empty">
          <CardHeader>
            <CardTitle className="text-lg">{t("schedule.emptyTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            <p>{t("schedule.emptyHint")}</p>
            {canManage && (
              <Button
                className="mt-3"
                onClick={() => setUploadOpen(true)}
                data-testid="schedule-empty-upload"
              >
                <Upload className="mr-2 h-4 w-4" />
                {t("schedule.upload.short")}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Grid */}
      {!loading && grid && (
        <Card data-testid="schedule-grid-card">
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full border-separate border-spacing-0 text-xs">
              <thead className="sticky top-0 z-10 bg-card">
                <tr>
                  <th className="sticky left-0 z-20 w-[200px] border-b border-r border-border bg-card px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {t("schedule.cols.name")}
                  </th>
                  {grid.days.map((d) => (
                    <th
                      key={d}
                      className="min-w-[110px] border-b border-border bg-card px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground"
                    >
                      {fmtHeader(d, locale)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((row, idx) => {
                  if (row.kind === "section") {
                    return (
                      <tr key={`sec-${row.section.id}-${idx}`}>
                        <td
                          colSpan={grid.days.length + 1}
                          className="border-b border-border px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white"
                          style={{
                            backgroundColor: row.section.color || "#1E293B",
                          }}
                        >
                          {row.section.name}
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr
                      key={`emp-${row.norm}-${idx}`}
                      className="hover:bg-muted/30"
                    >
                      <td className="sticky left-0 z-10 border-b border-r border-border bg-card px-3 py-1.5 text-xs font-medium">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate">{row.name}</span>
                          {row.role && row.role !== "regular" && (
                            <span
                              className={`inline-flex items-center gap-1 rounded border px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider ${
                                ROLE_BADGE_CLASS[row.role] || ""
                              }`}
                              title={roleLabel(t, row.role)}
                              data-testid={`schedule-role-badge-${row.norm}`}
                            >
                              <span>{ROLE_ICON[row.role]}</span>
                              <span>{roleLabel(t, row.role)}</span>
                            </span>
                          )}
                        </div>
                      </td>
                      {grid.days.map((d) => {
                        const e = row.days[d];
                        const cellOpen = openCellId === e?.id;
                        const cellContent = (
                          <div
                            className={`flex h-full min-h-[44px] cursor-${
                              editMode && canManage ? "pointer" : "default"
                            } flex-col items-center justify-center gap-0.5 rounded ${
                              e?.is_off
                                ? "bg-pink-500/10 text-pink-400"
                                : e
                                ? ""
                                : "text-muted-foreground/30"
                            }`}
                            data-testid={`schedule-cell-${row.norm}-${d}`}
                          >
                            {e?.is_off ? (
                              <div className="flex items-center gap-1 text-[11px] font-semibold">
                                <CalendarOff className="h-3 w-3" />
                                OFF
                              </div>
                            ) : e ? (
                              <>
                                <div className="flex items-center gap-1 text-[10px]">
                                  <Clock className="h-2.5 w-2.5 text-emerald-500" />
                                  <span>{fmtTime12(e.time_in)}</span>
                                </div>
                                <div className="text-[10px] text-muted-foreground">
                                  {fmtTime12(e.time_out)}
                                </div>
                                {e.manually_edited && (
                                  <Badge
                                    variant="outline"
                                    className="px-1 py-0 text-[8px]"
                                    title={t("schedule.cell.editedTooltip")}
                                  >
                                    <Pencil className="h-2 w-2" />
                                  </Badge>
                                )}
                              </>
                            ) : (
                              <span className="text-[10px]">—</span>
                            )}
                          </div>
                        );
                        return (
                          <td
                            key={`${row.norm}-${d}`}
                            className="border-b border-border p-1 text-center align-middle"
                          >
                            {editMode && canManage && e ? (
                              <CellEditPopover
                                open={cellOpen}
                                onOpenChange={(v) =>
                                  setOpenCellId(v ? e.id : null)
                                }
                                entry={e}
                                onSave={(patch) => handleSaveCell(e, patch)}
                                trigger={
                                  <button
                                    type="button"
                                    className="block w-full rounded transition hover:bg-accent/30"
                                  >
                                    {cellContent}
                                  </button>
                                }
                              />
                            ) : (
                              cellContent
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Sticky footer with totals */}
      {!loading && data && (
        <div
          className="sticky bottom-2 mt-4 rounded-lg border border-border bg-card/90 px-4 py-2 text-xs backdrop-blur"
          data-testid="schedule-totals"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-muted-foreground">
              {t("schedule.totals.entries", {
                n: (data.entries || []).length,
              })}
            </div>
            <div className="flex items-center gap-3">
              <span className="font-medium">
                {t("schedule.totals.scheduled")}:{" "}
                <span className="font-mono text-emerald-500">{totalHours}h</span>
              </span>
            </div>
          </div>
        </div>
      )}

      <ScheduleUploadModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUploaded={(res) => refresh(res?.schedule_id)}
      />
      <AnomaliesPanel
        open={anomaliesOpen}
        onOpenChange={setAnomaliesOpen}
        scheduleId={currentId}
      />
    </div>
  );
}
